import React from 'react';
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Mail, Building, MapPin, Calendar, Clock } from "lucide-react"
import ErrorBoundary from "@/components/common/ErrorBoundary"
import { SystemUser } from "@/pages/user/hooks/useUserManagement"
import { EmployeeStatus } from '@/contexts/employees/EmployeesContext';

interface UserProfileProps {
  user: SystemUser
  onClose: () => void
}

export function UserProfile({ user, onClose }: UserProfileProps) {
  if (!user) {
    return <div className="p-4">User data not available</div>;
  }

  return (
    <ErrorBoundary>
      <div className="space-y-3">
        {/* Compact Profile Header */}
        <div className="flex items-center space-x-3 pb-2 border-b">
          <Avatar className="h-12 w-12">
            <AvatarFallback className="text-lg">
              {user.name.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-semibold truncate">{user.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="capitalize text-xs">
                {user.role.replace('_', ' ')}
              </Badge>
              <Badge variant={user.status === EmployeeStatus.ACTIVE ? 'default' : 'secondary'} className="capitalize text-xs">
                {user.status}
              </Badge>
            </div>
          </div>
        </div>

        {/* Compact User Information */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <Mail className="h-3 w-3 text-muted-foreground flex-shrink-0" />
            <span className="font-medium w-20">Email:</span>
            <span className="truncate">{user.email}</span>
          </div>
          
          <div className="flex items-center gap-2 text-sm">
            <Building className="h-3 w-3 text-muted-foreground flex-shrink-0" />
            <span className="font-medium w-20">Department:</span>
            <span className="truncate">{user.department || 'Unassigned'}</span>
          </div>
          
          <div className="flex items-center gap-2 text-sm">
            <MapPin className="h-3 w-3 text-muted-foreground flex-shrink-0" />
            <span className="font-medium w-20">Position:</span>
            <span className="truncate">{user.position || 'Not specified'}</span>
          </div>
          
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-3 w-3 text-muted-foreground flex-shrink-0" />
            <span className="font-medium w-20">Created:</span>
            <span>{user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}</span>
          </div>
          
          <div className="flex items-center gap-2 text-sm">
            <Clock className="h-3 w-3 text-muted-foreground flex-shrink-0" />
            <span className="font-medium w-20">Updated:</span>
            <span>{user.updated_at ? new Date(user.updated_at).toLocaleDateString() : 'Unknown'}</span>
          </div>
        </div>
        
        {/* Compact Close Button */}
        <div className="flex justify-end pt-2 border-t">
          <Button onClick={onClose} size="sm" variant="outline">Close</Button>
        </div>
      </div>
    </ErrorBoundary>
  );
} 